<template>
  <teleport to="body">
    <div v-if="active" class="client-guide-overlay" data-client-guide-overlay>
      <svg class="client-guide-art" width="100%" height="100%" aria-hidden="true">
        <defs>
          <mask id="client-guide-cutout"><rect width="100%" height="100%" fill="white"/><rect :x="spot.x" :y="spot.y" :width="spot.width" :height="spot.height" rx="8" fill="black"/></mask>
          <marker id="client-guide-arrow" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 Z" fill="#0f766e"/></marker>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.25)" mask="url(#client-guide-cutout)" data-guide-shade/>
        <rect v-if="positioned" :x="spot.x" :y="spot.y" :width="spot.width" :height="spot.height" rx="8" fill="none" stroke="#0f766e" stroke-width="3"/>
        <path v-if="positioned" :d="arrow" stroke="white" stroke-width="7" fill="none"/>
        <path v-if="positioned" :d="arrow" stroke="#0f766e" stroke-width="3" fill="none" marker-end="url(#client-guide-arrow)" data-guide-arrow/>
      </svg>
      <section ref="dialog" class="client-guide-dialog" :style="cardStyle" role="dialog" aria-modal="true" aria-labelledby="client-guide-title" aria-describedby="client-guide-description">
        <div class="client-guide-heading">
          <span><portal-text message="CLIENT PORTAL GUIDE" :annotate="true" /></span><span aria-live="polite">{{ index + 1 }} / {{ steps.length }}</span>
        </div>
        <h2 id="client-guide-title" ref="title" tabindex="-1"><portal-text :message="step.title" :annotate="true" /></h2>
        <div id="client-guide-description" class="client-guide-copy">
          <p><portal-text :message="step.text" :annotate="true" /></p>
          <ul><li v-for="point in step.points" :key="point"><portal-text :message="point" :annotate="true" /></li></ul>
        </div>
        <div class="client-guide-actions">
          <button type="button" class="client-guide-skip" @click="finish"><portal-text message="Skip" :annotate="true" :help="false" /></button>
          <button type="button" class="client-guide-next" @click="next">{{ index === steps.length - 1 ? $t("Finish") : $t("Next") }} <i class="fas fa-arrow-right" aria-hidden="true"></i></button>
        </div>
      </section>
    </div>
    <div v-if="notice && !active" class="client-guide-notice" role="status">
      <portal-text :message="$message(notice)" :annotate="true" /> <button type="button" @click="retry"><portal-text message="Retry" :annotate="true" :help="false" /></button><button type="button" :aria-label="$t(&quot;Dismiss guide notice&quot;)" @click="notice = ''">×</button>
    </div>
  </teleport>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import steps from './clientGuideSteps';

export default {
  emits: ['active-change', 'prepare-step'],
  data: () => ({ steps, active: false, index: 0, positioned: false, confirming: false, manualRequested: false,
    notice: '', savePending: false, spot: { x: 0, y: 0, width: 0, height: 0 }, arrow: '', cardStyle: { left: '16px', top: '16px', visibility: 'hidden' } }),
  computed: { step() { return this.steps[this.index]; } },
  methods: {
    options() { return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 10000 }; },
    url(path = '') { return `${process.env.VUE_APP_BACKEND_DOMAIN}/api/client-guide${path}`; },
    async loadState() {
      try {
        const { data } = await axios.get(this.url(), this.options());
        if (this.unmounted) return;
        this.notice = '';
        if (data.auto_start && !this.manualRequested && !this.active) this.start();
      } catch (error) {
        if (!this.unmounted && !this.active && ![401, 403].includes(error.response?.status)) this.notice = 'The guide could not start. Use Guide or Retry to try again.';
      }
    },
    async requestReplay() {
      if (this.active || this.confirming) return;
      this.manualRequested = true; this.confirming = true;
      try {
        let closed;
        const closing = new Promise(resolve => { closed = resolve; });
        const result = await Swal.fire({ title: 'Replay the Client Portal guide?', icon: 'question', showCancelButton: true, confirmButtonText: 'Yes', cancelButtonText: 'No', focusCancel: true, didClose: closed });
        if (result.isConfirmed) { await closing; if (!this.unmounted) this.start(); }
      } finally { this.confirming = false; }
    },
    start() {
      if (this.active || this.unmounted) return;
      this.previousFocus = document.activeElement; this.previousOverflow = document.body.style.overflow;
      this.active = true; this.index = 0; this.notice = ''; this.positioned = false;
      this.$emit('active-change', true); document.body.style.overflow = 'hidden';
      window.addEventListener('resize', this.prepareStep); window.addEventListener('scroll', this.schedulePosition, true);
      document.addEventListener('keydown', this.keydown, true);
      this.prepareStep();
    },
    async prepareStep() {
      if (!this.active) return;
      this.$emit('prepare-step', this.step.section || null);
      await this.$nextTick();
      if (!this.active || this.unmounted) return;
      const target = document.querySelector(`[data-client-guide="${this.step.target}"]`);
      if (!target) { this.finish(); this.notice = 'This guide tab is unavailable. Please reload the portal and try Guide again.'; return; }
      target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      const scroll = target.closest('.sidebar-inner');
      if (scroll && window.innerWidth < 1024) scroll.scrollTop += target.getBoundingClientRect().top - Math.min(90, window.innerHeight * 0.18);
      this.position();
      if (this.observer) this.observer.disconnect();
      if (window.ResizeObserver) { this.observer = new window.ResizeObserver(this.schedulePosition); this.observer.observe(target); this.observer.observe(this.$refs.dialog); }
      this.$refs.title?.focus({ preventScroll: true });
    },
    schedulePosition() {
      if (this.frame || !this.active) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.position(); });
    },
    position() {
      if (!this.active || !this.$refs.dialog) return;
      const target = document.querySelector(`[data-client-guide="${this.step.target}"]`); if (!target) return;
      const rect = target.getBoundingClientRect(), vw = window.innerWidth, vh = window.innerHeight, gap = 36, pad = 12;
      this.spot = { x: Math.max(2, rect.left - 5), y: Math.max(2, rect.top - 4), width: Math.min(rect.width + 10, vw - Math.max(2, rect.left - 5) - 2), height: rect.height + 8 };
      const box = this.$refs.dialog.getBoundingClientRect(), width = box.width;
      let height = box.height, maxHeight = 'calc(100dvh - 24px)';
      const clamp = (value, min, max) => Math.max(min, Math.min(value, Math.max(min, max)));
      let x, y, sx, sy, ex, ey;
      if (rect.right + gap + width <= vw - pad) {
        x = rect.right + gap; y = clamp(rect.top - 22, pad, vh - height - pad);
        sx = x - 3; sy = clamp(rect.top + rect.height / 2, y + 22, y + height - 22); ex = rect.right + 9; ey = rect.top + rect.height / 2;
      } else if (rect.left - gap - width >= pad) {
        x = rect.left - gap - width; y = clamp(rect.top - 22, pad, vh - height - pad);
        sx = x + width + 3; sy = clamp(rect.top + rect.height / 2, y + 22, y + height - 22); ex = rect.left - 9; ey = rect.top + rect.height / 2;
      } else {
        x = clamp((vw - width) / 2, pad, vw - width - pad);
        const belowSpace = vh - pad - rect.bottom - gap, aboveSpace = rect.top - gap - pad;
        const below = belowSpace >= aboveSpace;
        const availableHeight = Math.max(140, Math.min(vh * 0.6, below ? belowSpace : aboveSpace));
        height = Math.min(height, availableHeight); maxHeight = `${availableHeight}px`;
        y = below ? rect.bottom + gap : clamp(rect.top - gap - height, pad, vh - height - pad);
        sx = clamp(rect.left + rect.width / 2, x + 24, x + width - 24); sy = below ? y - 3 : y + height + 3;
        ex = rect.left + rect.width / 2; ey = below ? rect.bottom + 9 : rect.top - 9;
      }
      this.cardStyle = { left: `${Math.round(x)}px`, top: `${Math.round(y)}px`, maxHeight, visibility: 'visible' };
      this.arrow = `M ${sx} ${sy} L ${ex} ${ey}`; this.positioned = true;
    },
    async next() {
      if (this.index === this.steps.length - 1) return this.finish();
      this.index++; await this.prepareStep();
    },
    keydown(event) {
      if (!this.active) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.finish(); return; }
      if (event.key !== 'Tab') return;
      const buttons = [...this.$refs.dialog.querySelectorAll('button:not([disabled])')], first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || !buttons.includes(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !buttons.includes(document.activeElement))) { event.preventDefault(); first.focus(); }
    },
    cleanup() {
      window.removeEventListener('resize', this.prepareStep); window.removeEventListener('scroll', this.schedulePosition, true);
      document.removeEventListener('keydown', this.keydown, true);
      this.observer?.disconnect(); if (this.frame) cancelAnimationFrame(this.frame); this.frame = null;
    },
    async finish() {
      if (!this.active) return;
      this.active = false; this.cleanup(); document.body.style.overflow = this.previousOverflow || '';
      this.$emit('active-change', false); await this.$nextTick();
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      await this.saveDismissal();
    },
    async saveDismissal() {
      this.savePending = true;
      try { await axios.post(this.url('/dismiss'), {}, this.options()); this.savePending = false; this.notice = ''; }
      catch (error) { if (!this.unmounted && ![401, 403].includes(error.response?.status)) this.notice = 'Guide closed. Your preference could not be saved, so it may appear again next visit.'; }
    },
    retry() { if (this.savePending) this.saveDismissal(); else this.loadState(); },
  },
  mounted() { this.loadState(); },
  beforeUnmount() { this.unmounted = true; this.cleanup(); if (this.active) document.body.style.overflow = ''; },
};
</script>

<style scoped>
.client-guide-overlay { position:fixed; inset:0; z-index:10000; isolation:isolate; }
.client-guide-art { position:absolute; inset:0; pointer-events:none; overflow:visible; }
.client-guide-dialog { position:fixed; display:flex; flex-direction:column; width:430px; max-width:calc(100vw - 24px); max-height:calc(100dvh - 24px); background:white; color:#334155; padding:22px; border-radius:14px; border:1px solid #cbd5e1; box-shadow:0 16px 45px rgba(0,0,0,.25); }
.client-guide-heading { display:flex; justify-content:space-between; gap:12px; font-size:11px; font-weight:800; letter-spacing:.07em; color:#0f766e; }
.client-guide-dialog h2 { font-size:21px; line-height:1.3; font-weight:800; margin:12px 0; flex-shrink:0; }
.client-guide-dialog h2:focus { outline:none; }
.client-guide-copy { min-height:0; overflow-y:auto; overscroll-behavior:contain; font-size:14px; line-height:1.6; }
.client-guide-copy p { margin:0 0 12px; }
.client-guide-copy ul { padding-left:19px; margin:0; list-style:disc; }
.client-guide-copy li { margin:0 0 10px; }
.client-guide-actions { display:flex; justify-content:space-between; gap:12px; padding-top:16px; flex-shrink:0; }
.client-guide-actions button { min-height:44px; min-width:90px; padding:10px 18px; border-radius:8px; font-weight:700; }
.client-guide-skip { border:1px solid #cbd5e1; background:white; color:#475569; }
.client-guide-next { background:#0f766e; color:white; }
.client-guide-next i { margin-left:8px; }
.client-guide-actions button:focus-visible,.client-guide-notice button:focus-visible { outline:3px solid #38bdf8; outline-offset:3px; }
.client-guide-notice { position:fixed; right:16px; bottom:16px; max-width:calc(100vw - 32px); z-index:10001; padding:12px 16px; border:1px solid #cbd5e1; background:white; border-radius:8px; box-shadow:0 4px 14px #0002; font-size:14px; }
.client-guide-notice button { margin-left:10px; text-decoration:underline; }
@media (max-width:1023px) {
  .client-guide-dialog { max-height:60dvh; width:430px; padding:18px; }
  .client-guide-dialog h2 { font-size:19px; margin:10px 0; }
}
@media (prefers-reduced-motion:reduce) { .client-guide-dialog { scroll-behavior:auto; } }
</style>
